// Libraries
import { animate, state, style, transition, trigger } from '@angular/animations';
import { CommonModule } from '@angular/common';
import { Component, effect, inject, OnDestroy, OnInit, Signal, signal, WritableSignal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxChange, MatCheckboxModule } from '@angular/material/checkbox';
import { MatOptionModule, MatRippleModule } from '@angular/material/core';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTabsModule } from '@angular/material/tabs';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { addDays, addHours, addMinutes, isBefore, isEqual, isWithinInterval, parseISO, startOfDay, subHours } from 'date-fns';
import { BehaviorSubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

// Services
import { AttachmentService } from 'src/app/services/api/attachments/attachment.service';
import { MetricService } from 'src/app/services/api/metrics/metric.service';
import { ProjectService } from 'src/app/services/api/projects/project.service';
import { TrackerService } from 'src/app/services/api/trackers/tracker.service';
import { UnitService } from 'src/app/services/api/units/unit.service';
import { AuthService } from 'src/app/services/core/auth.service';
import { WebSocketService } from 'src/app/services/core/web-socket.service';

// Interfaces
import { Attachment } from 'src/app/interfaces/api/attachment';
import { Event } from 'src/app/interfaces/api/event';
import { Metric } from 'src/app/interfaces/api/metric';
import { EnableTag, NormalizedSchedule, Project, PutProject } from 'src/app/interfaces/api/project';
import { Tracker } from 'src/app/interfaces/api/tracker';
import { Unit } from 'src/app/interfaces/api/unit';
import { User } from 'src/app/interfaces/api/user';
import { AvailableResource } from 'src/app/interfaces/shared/timeline';

// Classes
import { Tools } from 'src/app/classes/shared/tools';

// Tools
import { ConvertEventPipe } from 'src/app/pipes/convert-event.pipe';

// UI
import { NoDataComponent } from 'projects/ui-library/src/lib/components/no-data/no-data.component';
import { ButtonComponent, DatePickerComponent, RangePickerComponent, SearchBarComponent, SpinnerComponent } from 'ui-library';

// Components
import { CraneCyclesComponent } from 'src/app/components/project-pages/report/crane-cycles/crane-cycles.component';
import { EventListComponent } from 'src/app/components/project-pages/report/event-list/event-list.component';
import { SequenceCalendarComponent } from 'src/app/components/project-pages/report/sequence-calendar/sequence-calendar.component';
import { TimelineComponent } from 'src/app/components/project-pages/report/timeline/timeline.component';
import { MetricAddComponent } from 'src/app/components/shared/metric-add/metric-add.component';

@Component({
  selector: 'app-report',
  templateUrl: './report.component.html',
  styleUrls: ['./report.component.scss'],
  standalone: true,
  imports: [
    ButtonComponent,
    CommonModule,
    FormsModule,
    MatToolbarModule,
    MatIconModule,
    MatSelectModule,
    MatOptionModule,
    MatFormFieldModule,
    MatButtonModule,
    MatMenuModule,
    MatCheckboxModule,
    MatInputModule,
    MatRippleModule,
    MatSlideToggleModule,
    SpinnerComponent,
    MatTabsModule,
    TranslateModule,
    CraneCyclesComponent,
    EventListComponent,
    TimelineComponent,
    ConvertEventPipe,
    NoDataComponent,
    DatePickerComponent,
    RangePickerComponent,
    SequenceCalendarComponent,
    ButtonComponent,
    SearchBarComponent,
    MatTooltipModule
  ],
  animations: [
    trigger('slideInOut', [
      state('*', style({ width: '220px', opacity: 1 })),
      transition(':enter', [
        style({ width: '0px', opacity: 0 }),
        animate('400ms cubic-bezier(.4,0,.2,1)', style({ width: '220px', opacity: 1 })),
      ]),
      transition(':leave', [
        animate('400ms cubic-bezier(.4,0,.2,1)', style({ width: '0px', opacity: 0 })),
      ]),
    ]),
  ],
})

export class ReportComponent implements OnInit, OnDestroy {
  // Services
  private readonly metricService: MetricService = inject(MetricService);
  private readonly projectService: ProjectService = inject(ProjectService);
  private readonly trackerService: TrackerService = inject(TrackerService);
  private readonly unitService: UnitService = inject(UnitService);
  private readonly attachmentService: AttachmentService = inject(AttachmentService);
  private readonly route: ActivatedRoute = inject(ActivatedRoute);
  private readonly router: Router = inject(Router);
  private readonly socket: WebSocketService = inject(WebSocketService);
  private readonly authService: AuthService = inject(AuthService);
  private readonly tools: Tools = inject(Tools);
  public readonly dialog: MatDialog = inject(MatDialog);

  // Variables
  loading: Boolean = false;
  currentTab: any;

  timelineData: { attachments: any[], events: any[], metrics: any[], [key: string]: any[] } = {
    attachments: [],
    events: [],
    metrics: [],
  };

  lastUpdate!: string;
  timelineDataByTracker: Record<string, any> = {};

  eventTypes = [
    'Generated event',
    'Flow event',
    'User defined',
    'Tracker log',
    'Risk',
  ];

  selectedEventTypes$: BehaviorSubject<string[]> = new BehaviorSubject<string[]>(this.eventTypes);
  metricTypes: string[] = [];
  selectedMetricTypes$: BehaviorSubject<string[]> = new BehaviorSubject<string[]>(this.metricTypes);
  timelinesWithAttachment: any[] = [];
  selectedTrackerTypes$: BehaviorSubject<string[]> = new BehaviorSubject<string[]>([]);
  selectedResources$: BehaviorSubject<string[]> = new BehaviorSubject<string[]>([]);
  availableResources$: BehaviorSubject<AvailableResource[]> = new BehaviorSubject<AvailableResource[]>([]);

  metrics: Metric[] = [];
  events: Event[] = [];
  attachments: Attachment[] = [];
  trackers: Tracker[] = [];
  units: Unit[] = [];
  availableResources: any[] = [];

  metricSub: any = null;
  attachmentSub: any = null;
  eventSub: any = null;
  trackerToShow: any[] = [];

  mode: string = 'timeline';
  startDate: any;
  endDate: any;
  tabIndex: number = 0;
  searchText: string = "";
  validSearchText: BehaviorSubject<string> = new BehaviorSubject<string>('');
  showEndDate = false;
  startDateChanged = true;
  noData = true;
  schedule: NormalizedSchedule | null = null;
  trackerTypes: string[] = [];
  unitTypes: string[] = [];
  singleTimeline = false;

  craneTags: EnableTag[] = [];
  selectedTag: string = 'default';
  selectedTag$: BehaviorSubject<string> = new BehaviorSubject("");
  scroller$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  datesUpdater$: BehaviorSubject<string> = new BehaviorSubject(startOfDay(new Date()).toISOString());
  destroy$ = new Subject<void>();
  private _canNavigateNext: boolean = true;

  // Signals
  readonly currentProject: Signal<Project> = this.projectService.currentProjectSignal;
  readonly currentUser: Signal<User> = this.authService.currentUser;
  readonly activitySignal: WritableSignal<any> = signal(null);
  readonly timelineUpdateSignal: WritableSignal<any> = signal(null);
  target: WritableSignal<string> = signal<string>('');

  // Computeds
  get canNavigateNext(): boolean {
    return this._canNavigateNext;
  }

  constructor() {
    let tabIndex = this.route.snapshot.queryParamMap.get('tab');
    if (tabIndex) this.tabIndex = parseInt(tabIndex);

    let mode = this.route.snapshot.queryParamMap.get('mode');
    if (mode) this.mode = mode;

    let startDate = this.route.snapshot.queryParamMap.get('startDate');
    let endDate = this.route.snapshot.queryParamMap.get('endDate');
    let target = this.route.snapshot.queryParamMap.get('target');

    if (startDate)
      this.startDate = new Date(startDate).toISOString();

    if (endDate)
      this.endDate = new Date(endDate).toISOString();

    if (target)
      this.target.set(target);

    this.datesUpdater$.next(this.startDate);

    this.metricService.getMetricNames().pipe(takeUntil(this.destroy$)).subscribe((res: any) => {
      if (res && res.length) {
        this.metrics = res.map((m: Metric) => {
          return {
            name: m._id,
            payload: {},
          };
        });

        this.metricTypes = res.map((m: Metric) => m._id);
        this.selectedMetricTypes$.next(res.map((m: Metric) => m._id));
      }
    });

    // 🎯 Effect: Change project
    effect(() => {
      const project = this.currentProject();

      if (!project) return;

      if (project?.enableTags && project.enableTags.length) {
        const seenNames = new Set<string>();

        this.craneTags = project.enableTags
          .filter((t: EnableTag) => t.enabled && t.name !== '')
          .filter((t: EnableTag) => {
            if (seenNames.has(t.name)) {
              return false;
            }

            if (t.module === 'crane' || !t.module) {
              seenNames.add(t.name);
              return true;
            }

            return false;
          });

        this.craneTags.sort((a, b) => (a.name.toLowerCase() < b.name.toLowerCase() ? -1 : 1));
      } else {
        this.attachmentService.getTags('crane').pipe(takeUntil(this.destroy$)).subscribe((res: any[]) => {
          this.craneTags = res;
          this.craneTags.sort((a, b) => (a.name.toLowerCase() < b.name.toLowerCase() ? -1 : 1));
        });
      }

      this.cleanup();

      let tabIndex = this.route.snapshot.queryParamMap.get('tab');
      if (tabIndex) this.tabIndex = parseInt(tabIndex);

      let mode = this.route.snapshot.queryParamMap.get('mode');
      if (mode) this.mode = mode;

      startDate = this.route.snapshot.queryParamMap.get('startDate');
      endDate = this.route.snapshot.queryParamMap.get('endDate');

      if (startDate) {
        this.startDate = startDate;
      }

      if (endDate) {
        this.endDate = endDate;
      }

      if (!project.timelines)
        project.timelines = [];

      if (project.singleTimeline !== undefined)
        this.singleTimeline = project.singleTimeline;

      let schedule = project.schedule;
      this.schedule = this.tools.normalizeSchedule(schedule);

      if (!startDate || !endDate) {
        if (startDate && project?.createdDate && startDate < project.createdDate)
          startDate = String(project.createdDate);

        let dates = this.tools.getWorkingHours(
          startDate || new Date().toISOString(),
          this.schedule, project.timezone
        );

        this.startDate = dates.startDate;
        this.endDate = dates.endDate;
      }

      this.datesUpdater$.next(this.startDate);
      this.initDates();

      this.activitySignal.set(null);

      this.projectService.getActivity({ databases: [project.database] }).pipe(takeUntil(this.destroy$)).subscribe((activity: any) => {
        this.activitySignal.set(activity);
      });

      this.trackerService.getTrackers().pipe(takeUntil(this.destroy$)).subscribe((trackers: Tracker[]) => {
        this.trackers = trackers;

        this.unitService.getUnits({}).pipe(takeUntil(this.destroy$)).subscribe((units: Unit[]) => {
          this.units = units;

          if (this.mode == "timeline") {
            if (startDate && endDate) {
              this.getTimelineData(endDate, this.mode == "timeline");
            } else {
              this.getTimelineData(this.lastUpdate || this.endDate, this.mode == "timeline");
            }
          } else if (mode == "tags") {
            // this.selectedMetricTypes$.next(this.metricTypes);
            // this.getTagData()
            // this.getListData();
            // this.metricService.getTimelineData({ startDate: moment(this.startDate).toISOString(), endDate: moment(endDate ?? this.endDate).toISOString(), limit: 1000, populated: true }).pipe(takeUntil(this.destroy$)).subscribe(res => {
            //   if (res) {
            //     this.noData = false;
            //   }
            // })
          } else if (mode == "list" || mode == "calendar") {
            this.getListData();
          }
        });
      })

      this.getMetricNames();
    });

    // 🔄 Effect: Update timeline changes
    effect(() => {
      const data = this.timelineUpdateSignal();

      if (data) {
        this.onTimelineDataUpdated(data);
      }
    });
  }

  /////////////////////////////
  // Angular lifecycle hooks //
  /////////////////////////////

  ngOnInit(): void {
    this.initSockets();
    const queryParamMap = this.route.snapshot.queryParamMap;
    let startDate = queryParamMap.get('startDate');
    let endDate = queryParamMap.get('endDate');

    if (startDate && endDate)
      this.showEndDate = true;

    if (!this.startDate) {
      this.startDate = addHours(startOfDay(new Date()), 7).toISOString();
    }

    if (!this.endDate) {
      this.endDate = addHours(startOfDay(parseISO(this.startDate)), 20).toISOString();
    }

    this.verifCanNavigateNext();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  initDates() {
    if (!this.startDate)
      this.startDate = addHours(startOfDay(new Date()), 7).toISOString();

    if (!this.endDate)
      this.endDate = addHours(startOfDay(parseISO(this.startDate)), 20).toISOString();
  }

  cleanup() {
    if (this.metricSub)
      this.metricSub.unsubscribe();

    if (this.eventSub)
      this.eventSub.unsubscribe();

    if (this.attachmentSub)
      this.attachmentSub.unsubscribe();

    this.metrics = [];
    this.events = [];
    this.attachments = [];
    this.trackerTypes = [];
    this.unitTypes = [];

    this.timelineData = {
      attachments: [],
      events: [],
      metrics: [],
    };

    this.availableResources = [];
  }

  initSockets() {
    if (!this.endDate || isBefore(parseISO(this.endDate), subHours(new Date(), 1))) return;

    this.metricSub = this.socket.getUpdate("Metric").pipe(takeUntil(this.destroy$))
      .subscribe((data: Metric[]) => {
        data.forEach((metric) => {
          if (this.startDate && this.endDate) {
            if (isWithinInterval(new Date(metric.createdDate), { start: new Date(this.startDate), end: new Date(this.endDate) })) {
              this.metrics.push(metric);
            }
          } else {
            this.metrics.push(metric);
          }
        });

        if (data.length) {
          this.parseTimelineData(this.metrics, 'metrics');
          this.timelineData = { ...this.timelineData };
        }
      });

    this.attachmentSub = this.socket.getUpdate("Attachment").pipe(takeUntil(this.destroy$))
      .subscribe((data: Attachment[]) => {
        data.forEach((attachment) => {
          if (attachment.tracker) {
            let tracker = this.trackers.find((t) => t.id == attachment.tracker);

            if (tracker) {
              attachment.tracker = tracker.name;
            }
          }

          const parsedAttachment = this.parseAttachment(attachment);

          if (this.startDate && this.endDate) {
            if (isWithinInterval(new Date(attachment.createdDate), { start: new Date(this.startDate), end: new Date(this.endDate) })) {
              this.attachments.push(parsedAttachment);
            }
          } else {
            this.attachments.push(parsedAttachment);
          }
        });

        if (data.length) {
          this.parseTimelineData(this.attachments, 'attachments');
          this.timelineData = { ...this.timelineData };
        }
      });

    this.eventSub = this.socket.getUpdate("Event").pipe(takeUntil(this.destroy$))
      .subscribe((data: Event[]) => {
        data.forEach((event) => {
          if (event.tracker) {
            let tracker = this.trackers.find((t) => t.id == event.tracker);

            if (tracker) {
              event.tracker = tracker.name;
            }
          }

          const eventData: any = { name: 'Events', payload: event };

          if (this.startDate && this.endDate) {
            if (isWithinInterval(new Date(event.startDate), { start: new Date(this.startDate), end: new Date(this.endDate) })) {
              this.events.push(eventData);
            }
          } else {
            this.events.push(eventData);
          }
        });

        if (data.length) {
          this.parseTimelineData(this.events, 'events');
          this.timelineData = { ...this.timelineData };
        }
      });
  }

  getMetricNames() {
    this.metricService.getMetricNames().pipe(takeUntil(this.destroy$)).subscribe((res: Metric[]) => {
      if (res && res.length) {
        const types = res.map((m: Metric) => m._id).filter((id): id is string => id !== undefined);

        //Limit visibility of risk metrics to admin & userAdmin
        if (this.isBaseUser()) {
          this.metricTypes = types.filter(
            (type: string) => type && type.toLowerCase() !== 'risk'
          );

          this.eventTypes = this.eventTypes.filter(
            (type: string) => type.toLowerCase() !== 'risk'
          );

          this.selectedEventTypes$.next(this.eventTypes);
        } else {
          this.metricTypes = types;
        }

        this.selectedMetricTypes$.next(this.metricTypes);
      }
    });
  }

  search(text: string = "") {
    if (text !== this.validSearchText.value) {
      this.validSearchText.next(text);
    }
  }

  navigateDate(offset: number) {
    const currentDate = new Date();

    const startDateObj = parseISO(this.startDate);
    const endDateObj = parseISO(this.endDate);

    const newStartDate = addDays(startDateObj, offset);
    const newEndDate = addDays(endDateObj, offset);

    if (isBefore(newStartDate, currentDate) || isBefore(newEndDate, currentDate)) {
      this.startDate = newStartDate.toISOString();
      this.endDate = newEndDate.toISOString();
      this.getTimelineData(newEndDate.toISOString());
    } else {
      this.startDate = startDateObj.toISOString();
      this.endDate = endDateObj.toISOString();
    }


    this.datesUpdater$.next(this.startDate);

    const queryParams: Params = {
      mode: this.mode,
      startDate: this.startDate || undefined,
      endDate: this.showEndDate ? this.endDate : undefined,
    };

    this.router.navigate([], {
      queryParams: queryParams,
      queryParamsHandling: 'merge',
    });

    this.verifCanNavigateNext();
  }

  verifCanNavigateNext(): void {
    const today = startOfDay(new Date());
    const currentDisplayedDate = this.showEndDate ? parseISO(this.endDate) : parseISO(this.startDate);
    const nextDate = startOfDay(addDays(currentDisplayedDate, 1));
    this._canNavigateNext = isBefore(nextDate, today) || nextDate.getTime() === today.getTime();
  }

  setMode(mode: string) {
    if (mode == 'timeline') {
      this.getTimelineData();
    } else if (mode == 'list') {
      this.getListData();
    } else if (mode == 'calendar') {
      console.log("set calendar")
    }

    this.mode = mode;

    const queryParams: Params = {
      mode: this.mode,
      startDate: this.startDate || undefined,
      endDate: this.showEndDate ? this.endDate : undefined,
    };

    this.router.navigate([], {
      queryParams: queryParams,
      queryParamsHandling: 'merge',
    });

    this.verifCanNavigateNext();
  }

  getListData(endDate: string | null = null) {
    this.getTimelineData(endDate, false);
  }

  onTimelineResourceChanged(event: MatCheckboxChange, resource: any) {
    const project = this.currentProject();
    let resourcesChanged: boolean = false;

    this.availableResources.forEach((r) => {
      const sameTracker = resource.tracker && r.tracker == resource.tracker;
      const sameUnit = resource.unit && r.unit == resource.unit;

      if (sameTracker || sameUnit) {
        r.show = event.checked;
        resourcesChanged = true;
      }
    });

    if (resourcesChanged)
      this.availableResources$.next(
        this.availableResources.filter((r) => r.show)
      );

    if (!project?.timelines) {
      return;
    }

    const timelineIndex = project.timelines.findIndex((t: any) =>
      (resource.tracker && t.tracker == resource.tracker) ||
      (resource.unit && t.unit == resource.unit) ||
      t.name == resource.name
    );

    if (timelineIndex !== -1 && project.timelines[timelineIndex]) {
      project.timelines[timelineIndex]['show'] = event.checked;
    } else {
      project.timelines.push({
        tracker: resource.tracker,
        unit: resource.unit,
        name: resource.name,
        type: resource.type,
        show: event.checked,
      });
    }

    const json: PutProject = {
      id: project?.id ?? '',
      timelines: project?.timelines,
    };

    this.projectService.setProject(json).pipe(takeUntil(this.destroy$)).subscribe((res: any) => {
      if (project) {
        this.projectService.setCurrentProject(project);
      }
    });
  }

  displayTimelines() {
    this.currentProject()?.timelines?.forEach((t: any) => {
      this.availableResources.forEach((res: any) => {
        const sameTracker = res.tracker && t.tracker == res.tracker;
        const sameUnit = res.unit && t.unit == res.unit;

        if (t.show == false && (sameTracker || sameUnit || t.name == res.name))
          res.show = false;
      });
    });
  }

  getUnitTrackers(unit: Unit): Tracker[] {
    return (unit.trackers || [])
      .map((t: string | Tracker) => {
        const id = typeof t === 'string' ? t : t.id;
        return this.trackers.find((tr) => tr.id == id);
      })
      .filter((t): t is Tracker => !!t);
  }

  addTracker(obj: any) {
    let tracker = this.trackers.find((t: Tracker) => {
      if (obj.trackerName)
        return t.name == obj.trackerName;

      if (obj.tracker && obj.tracker.name)
        return t.name == obj.tracker.name;

      if (obj.tracker && !obj.tracker.name)
        return t.id == obj.tracker || t.name == obj.tracker;

      if (obj.trackers?.length)
        return obj.trackers.includes(t.id);

      return false;
    });

    let unit: Unit | undefined;

    if (tracker) {
      unit = this.units.find((u: Unit) => {
        return u.trackers?.find((t: string | Tracker) => {
          const trackerId = typeof t === 'string' ? t : t.id;

          return trackerId === tracker.id;
        });
      });

      let trackerIndex = this.trackerTypes.indexOf(tracker.name);

      if (trackerIndex == -1) {
        this.trackerTypes.push(tracker.name);
        this.selectedTrackerTypes$.next(this.trackerTypes);
      }
    }

    if (!tracker && obj.userEmail) {
      if (!this.availableResources.find((r) => r.tracker == 'user')) {
        this.availableResources.push({
          name: 'user',
          type: 'user',
          tracker: 'user',
          trackerObj: {},
          unitObj: {},
          show: true,
        });
      }
    }

    if (unit) {
      if (!this.availableResources.find((r) => r.type == 'unit' && r.unit == unit?.id))
        this.availableResources.push({
          name: unit.name,
          type: 'unit',
          unit: unit.id,
          unitObj: unit,
          trackers: this.getUnitTrackers(unit),
          show: true,
        });
    } else if (tracker) {
      if (!this.availableResources.find((r) => r.tracker == tracker.id))
        this.availableResources.push({
          name: tracker.name,
          type: 'tracker',
          tracker: tracker.id,
          trackerObj: tracker,
          show: true,
        });
    } else if (!tracker && obj.unitName) {
      unit = this.units.find((u) => u.name == obj.unitName);

      if (!unit) return;

      let unitIndex = this.unitTypes.indexOf(unit.name);

      if (unitIndex == -1) {
        this.unitTypes.push(unit.name);
        this.selectedResources$.next(this.unitTypes);
      }
    }
  }

  parseTags(a: any, events: any[] = []) {
    let predictionNames: Record<string, any[]> = {
      llm: [],
      crane: [],
      risk: [],
      worker: [],
      concreting: [],
      action: [],
      zone: []
    };

    let predictions: Record<string, any[]> = {
      llm: [],
      crane: [],
      risk: [],
      worker: [],
      concreting: [],
      action: [],
      zone: []
    }

    if (a.tags && Object.keys(a.tags).length)
      Object.keys(a.tags).forEach((key) => {
        let predictionArray = a.tags[key].filter((i: any) => !!i);
        let workers: number = 0;

        predictionArray.forEach((p: any) => {

          predictions[key]?.push(p);

          if (p.name) {
            p.tagName = p.name;
            delete p.name;
          }

          if (key == 'worker') workers++;

          if (predictionNames[key]?.indexOf(p.tagName) == -1 && p.tagName != 'Negative')
            predictionNames[key]?.push(p.tagName);

          if (p.description && key == 'risk' && !this.isBaseUser()) {
            let riskEventIndex = events.findIndex((event) => {
              if (!event.trackers?.length) return false;

              let trackerId = event.trackers[0].id || event.trackers[0];

              return ([a.tracker?.id, a.tracker].includes(trackerId) && event.endDate &&
                isWithinInterval(new Date(a.createdDate), { start: new Date(event.startDate), end: new Date(event.endDate) })
              );
            });

            if (riskEventIndex >= 0) {
              events[riskEventIndex].comment = p.description;
            }
          }
        });

        if (workers > 0 && predictionNames['worker']?.length)
          predictionNames['worker'] = [
            `${workers} ${predictionNames['worker']?.[0]}${workers > 1 ? 's' : ''
            }`,
          ];

        if (this.isBaseUser()) {
          predictionNames['risk'] = [];
          predictionNames['llm'] = [];
        }
      });

    return { predictionNames, predictions };
  }

  parseAttachment(a: any, events: any[] = []) {
    let { predictionNames, predictions } = this.parseTags(a, events);

    let customVisionIds = {
      crane: '',
      risk: '',
      worker: '',
      concreting: '',
      zone: '',
    };

    if (a.customVisionIds)
      customVisionIds = { ...customVisionIds, ...a.customVisionIds };

    let tracker: Tracker | string | undefined;
    let trackerName: string | Tracker | undefined;

    if (a.tracker) {
      if (a.tracker.id) {
        tracker = a.tracker.id;
        trackerName = a.tracker.name;
      } else if (this.tools.isHex(a.tracker)) tracker = a.tracker;
      else {
        tracker = tracker;
        trackerName = tracker;
      }
    }

    let unit: Unit | undefined;
    let unitName: string | undefined;

    if (a.unit) {
      if (a.unit.id) {
        unit = a.unit.id;
        unitName = a.unit.name;
      } else if (this.tools.isHex(a.unit)) unit = a.unit;
      else unitName = a.unit;
    }

    let rIndex = this.availableResources.findIndex(
      (r: any) =>
        r.tracker == tracker ||
        (tracker && r.trackers?.some((t: Tracker) => t.id == tracker)) ||
        (trackerName && r.type == 'tracker' && trackerName == r.name) ||
        (unitName && r.type == 'unit' && unitName == r.name)
    );

    let title = (rIndex >= 0 && this.availableResources[rIndex].name) || trackerName || unitName || tracker || unit || 'Attachments';

    let attachment: any = {
      name: 'Attachments',
      id: a.id,
      payload: {
        crane: predictionNames["crane"]?.join(`, `) || '',
        risks: predictionNames["risk"]?.join(", ") || '',
        action: predictionNames["action"]?.join(", ") || '',
        zone: predictionNames["zone"]?.join(", ") || '',
        workers: predictionNames["worker"]?.join(", ") || '',
        thumbnail: a.thumbnail,
        name: a.name,
        tags: predictions,
        picture: a.url,
        startDate: a.createdDate,
        endDate: addMinutes(new Date(a.createdDate), 1).toISOString(),
        title,
        processed: a.processed,
        customVisionIds,
      },
    };

    if (tracker)
      attachment.payload.tracker = tracker;

    if (trackerName)
      attachment.payload.trackerName = trackerName;

    if (unit)
      attachment.payload.unit = unit;

    if (unitName)
      attachment.payload.unitName = unitName;

    return attachment;
  }

  async getTimelineData(endDate: string | null = null, parseTimeline = true) {
    try {
      this.metrics = [];

      this.attachments = [
        { name: 'Attachments', payload: { title: 'Attachments' } } as any,
      ];

      this.events = [
        {
          name: 'Events',
          type: 'Generated event',
          payload: { title: 'Events' },
        } as any,
      ];

      this.loading = true;


      if (!endDate && !this.startDate && !this.endDate) {
        const { startDate, endDate } = this.tools.getWorkingHours(
          this.startDate,
          this.schedule,
          this.currentProject()?.timezone
        );

        this.startDate = new Date(startDate).toISOString();
        this.endDate = new Date(endDate).toISOString();
      }

      this.metricService.getTimelineData({
        startDate: this.startDate,
        endDate: endDate ?? this.endDate,
        limit: 10000,
        populated: true,
      }).pipe(takeUntil(this.destroy$)).subscribe((res: any) => {
        this.noData = true;

        if (res[0] && res[0].length) {
          this.metrics = res[0];

          this.metrics.forEach((m) => {
            if (m.tracker && this.trackers.find((t) => t.id == m.tracker))
              this.addTracker(m);
            else if (m.userEmail) this.addTracker(m);
            else this.addTracker(m.payload);
          });

          this.noData = false;
        }

        if (res[2] && res[2].length) {
          this.attachments = res[2].map((a: any) => {
            let attachment = this.parseAttachment(a, res[1] || []);
            this.addTracker(a);
            return attachment;
          });

          this.noData = false;
        }

        if (res[1] && res[1].length) {
          this.events = res[1].map((e: any) => {
            let event = {
              name: e.type,
              id: e._id || e.id,
              payload: e,
            };

            this.addTracker(e);
            return event;
          });

          this.noData = false;
        }

        this.displayTimelines();
        this.loading = false;

        if (parseTimeline) {
          if (this.attachments.length) {
            this.parseTimelineData(this.attachments, 'attachments');
          }

          this.parseTimelineData(this.events, 'events');
          this.parseTimelineData(this.metrics, 'metrics');
          this.timelineData = { ...this.timelineData };
        }

        this.availableResources$.next(
          this.availableResources.filter((r) => r.show)
        );
      });
    } catch (e) {
      console.error(e);
      this.loading = false;
    }
  }

  parseTimelineData(array: any[], type: any) {
    this.timelineData[type] = [];

    array.forEach((m: any) => {
      let line = this.timelineData[type]?.findIndex((t: any) => t.name == m.name) ?? -1;

      if (type == 'metrics' && !m.payload.startDate && !m.payload.endDate && m.payload.createdDate) {
        m.payload.startDate = new Date(m.payload.createdDate).toISOString();
        m.payload.endDate = addMinutes(new Date(m.payload.startDate), 1).toISOString();
      }
      if (type == 'metrics' && m.payload.startDate) {
        m.payload.startDate = new Date(m.payload.startDate).toISOString();
      }

      if (m.name == 'Risk' && this.isBaseUser()) {
        return;
      }

      if (type !== 'attachments' && !m.payload.startDate && !m.payload.endDate) {
        if (line == -1)
          this.timelineData[type]?.push({
            name: m.name,
            data: [],
            children: [],
          });

        return;
      }

      let date;

      if (m.payload && m.payload.startDate) {
        let endDate = addMinutes(new Date(m.payload.startDate), 1).toISOString();

        if (m.payload.endDate)
          endDate = m.payload.endDate;

        if (!m.payload.endDate)
          m.payload.endDate = endDate;

        if (m.payload.startDate == m.payload.endDate)
          endDate = addMinutes(new Date(m.payload.startDate), 1).toISOString();

        date = { startDate: m.payload.startDate, endDate };
      }

      if (m.payload.label && !isNaN(m.payload.label)) {
        if (m.payload.label % 1 > 0)
          m.payload.label = parseFloat(m.payload.label).toFixed(3);
      }

      m.payload.id = m.id;
      let payload = { ...m.payload, ...date };

      if (type == 'attachments' && !payload.id && !date) return;

      if (!m.payload.tracker && m.payload.trackers?.length) {
        payload.tracker = m.payload.trackers[0].id || m.payload.trackers[0];
      }

      if (m.tracker) {
        if (m.tracker.id) {
          payload.tracker = m.tracker.id;
          payload.trackerName = m.tracker.name;
        } else if (this.tools.isHex(m.tracker)) {
          payload.tracker = m.tracker;
        } else {
          payload.trackerName = m.tracker;
        }
      }

      if (!payload.trackerName && m.trackerName) {
        payload.trackerName = m.trackerName;
      }

      if (!m.payload.unit && m.payload.units?.length) {
        payload.unit = m.payload.units[0].id || m.payload.units[0];
      }

      if (m.unit) {
        if (m.unit.id) {
          payload.unit = m.unit.id;
          payload.unitName = m.unit.name;
        } else if (this.tools.isHex(m.unit)) {
          payload.unit = m.unit;
        } else {
          payload.unitName = m.unit;
        }
      }

      if (m.comment) {
        payload.comment = m.comment;
      }

      if (!payload.unitName && m.unitName)
        payload.unitName = m.unitName;

      let rIndex = this.availableResources.findIndex((r) =>
        r.tracker == payload.tracker ||
        (payload.tracker && r.trackers?.some((t: Tracker) => t.id == payload.tracker)) ||
        (payload.trackerName && r.type == 'tracker' && payload.trackerName == r.name) ||
        (payload.unitName && r.type == 'unit' && payload.unitName == r.name)
      );

      let title = (rIndex >= 0 && this.availableResources[rIndex].name) || payload.trackerName || payload.unitName || payload.tracker || payload.unit;

      if (!title) {
        payload.noChild = true;
        title = m.payload.title || m.name;
      }

      if (!m.payload.title) {
        payload.title = m.name;
        m.payload.title = m.name;
      }

      if (m.userEmail) {
        payload.userEmail = m.userEmail;
        m.payload.userEmail = m.userEmail;
      }

      if (!this.singleTimeline && line >= 0) {
        let child = this.timelineData[type]?.[line]?.children?.findIndex((c: any) => c.name == m.payload.title) ?? -1;

        if (child >= 0) {
          this.timelineData[type]?.[line]?.children?.[child]?.data?.push(payload);
          this.timelineData[type]?.[line]?.data?.push(payload);

          let granchild = !this.singleTimeline || type == 'attachments' ? -1 : this.timelineData[type]?.[line]?.children?.[child]?.children?.findIndex((c: any) => c.name == title) ?? -1;

          if (granchild >= 0) {
            this.timelineData[type]?.[line]?.children?.[child]?.children?.[granchild]?.data?.push({ ...payload, title });
          } else if (this.singleTimeline && type != 'attachments') {
            this.timelineData[type]?.[line]?.children?.[child]?.children?.push({
              name: title,
              data: [{ ...payload, title }],
            });
          }
        } else {
          this.timelineData[type]?.[line].data.push(payload);

          this.timelineData[type]?.[line].children.push({
            name: payload.title,
            data: [payload],
            children: !this.singleTimeline || type == 'attachments' ? [] :
              [
                {
                  name: title,
                  data: [{ ...payload, title }],
                },
              ],
          });
        }

        this.timelineData[type]?.[line].children.sort((a: any, b: any) =>
          a.name < b.name ? -1 : 1
        );
      } else if (!this.singleTimeline && line < 0) {
        this.timelineData[type]?.push({
          name: m.name,
          data: [payload],
          children: [
            {
              name: payload.title || m.name,
              data: [payload],
              children: !this.singleTimeline || type == 'attachments' ? [] :
                [
                  {
                    name: title,
                    data: [{ ...payload, title }],
                  },
                ],
            },
          ],
        });
      } else if (line >= 0) {
        let child = this.timelineData[type]?.[line]?.children?.findIndex((c: any) => c.name == title) ?? -1;

        if (child >= 0) {
          this.timelineData[type]?.[line]?.children?.[child].data.push(
            type == 'attachment' ? payload : { ...payload, title }
          );

          this.timelineData[type]?.[line].data.push(payload);

          let granchild = !this.singleTimeline || type == 'attachments' ? -1 : this.timelineData[type]?.[line]?.children?.[child]?.children?.findIndex((c: any) => c.name == m.payload.title) ?? -1;

          if (granchild >= 0) {
            this.timelineData[type]?.[line]?.children?.[child].children[
              granchild
            ].data.push(payload);
          } else if (this.singleTimeline && type != 'attachments') {
            this.timelineData[type]?.[line]?.children?.[child]?.children?.push({
              name: m.payload.title,
              data: [payload],
            });
          }

          if (type != 'attachments')
            this.timelineData[type]?.[line]?.children?.[child].children.sort(
              (a: any, b: any) => (a.name < b.name ? -1 : 1)
            );
        } else {
          this.timelineData[type]?.[line].data.push(payload);

          this.timelineData[type]?.[line].children.push({
            name: title,
            data: [type == 'attachments' ? payload : { ...payload, title }],
            children:
              !this.singleTimeline || type == 'attachments' ? [] :
                [
                  {
                    name: payload.title,
                    data: [payload],
                  },
                ],
          });
        }

        this.timelineData[type]?.[line].children.sort((a: any, b: any) =>
          a.name < b.name ? -1 : 1
        );
      } else {
        this.timelineData[type]?.push({
          name: m.name,
          data: [payload],
          children: [
            {
              name: title || payload.title || m.name,
              data: [{ ...payload, title }],
              children: !this.singleTimeline || type == 'attachments' ? [] :
                [
                  {
                    name: payload.title || m.name || title,
                    data: [payload],
                  },
                ],
            },
          ],
        });
      }
    });

    Object.keys(this.timelineData).forEach((key) => {
      this.timelineData[key]?.forEach((d, i) => {
        d.data.sort((a: any, b: any) =>
          isBefore(new Date(a.startDate), new Date(b.startDate)) ? 1 : -1
        );
      });
    });
  }

  onDatePickerChanged(newDate: string) {
    const startDate = new Date(newDate).toISOString();
    const endDate = addHours(startOfDay(parseISO(startDate)), 20).toISOString();
    this.startDateChanged = true;

    if (this.startDate != startDate) {
      this.startDate = startDate;
    }

    if (this.endDate != endDate) {
      this.endDate = endDate;
    }

    if (this.mode == 'timeline') {
      this.getTimelineData(endDate);
    } else {
      this.getListData(endDate);
    }

    this.datesUpdater$.next(this.startDate);

    const queryParams: Params = {
      mode: this.mode,
      startDate: this.startDate || undefined,
      endDate: this.showEndDate ? this.endDate : undefined,
    };

    this.router.navigate([], {
      queryParams: queryParams,
      queryParamsHandling: 'merge',
    });

    this.verifCanNavigateNext();
  }

  onRangePickerChanged(result: { startDate: string; endDate: string }) {
    if (!result.startDate || !result.endDate) return;

    if (typeof result.startDate != 'string') {
      this.startDateChanged = true;
    } else if (typeof result.endDate != 'string') {
      this.startDateChanged = false;
    }

    const newStartDate = new Date(result.startDate).toISOString();
    const newEndDate = new Date(result.endDate).toISOString();

    if (this.startDate != newStartDate) {
      this.startDate = newStartDate;
    }

    if (this.endDate != newEndDate) {
      this.endDate = newEndDate;
    }

    if (this.mode == 'timeline') {
      this.getTimelineData(result.endDate);
    } else {
      this.getListData(result.endDate);
    }

    this.datesUpdater$.next(this.startDate);

    const queryParams: Params = {
      mode: this.mode,
      startDate: this.startDate || undefined,
      endDate: this.showEndDate ? this.endDate : undefined,
    };

    this.router.navigate([], {
      queryParams: queryParams,
      queryParamsHandling: 'merge',
    });

    this.verifCanNavigateNext();
  }

  onEventTypeChanged(event: MatCheckboxChange, type: string) {
    const currentValue = this.selectedEventTypes$.value.slice();

    if (event.checked) {
      currentValue.push(type);
    } else {
      const index = currentValue.indexOf(type);
      if (index !== -1) {
        currentValue.splice(index, 1);
      }
    }

    this.selectedEventTypes$.next(currentValue);

    if (this.mode == 'timeline') {
      this.getTimelineData();
    } else {
      this.getListData();
    }
  }

  onMetricTypeChanged(event: MatCheckboxChange, type: string) {
    const currentValue = this.selectedMetricTypes$.value.slice();

    if (event.checked) {
      currentValue.push(type);
    } else {
      const index = currentValue.indexOf(type);
      if (index !== -1) {
        currentValue.splice(index, 1);
      }
    }

    this.selectedMetricTypes$.next(currentValue);
  }

  onTrackerTypeChanged(event: MatCheckboxChange, type: string) {
    const project = this.currentProject();
    const currentValue = this.selectedTrackerTypes$.value.slice();
    if (project) {
      if (!project.timelines) {
        project.timelines = [];
      }
    }

    const timelineIndex = project?.timelines?.findIndex((timeline) => timeline['name'] == type) ?? -1;

    if (timelineIndex !== -1 && project?.timelines?.[timelineIndex]) {
      project.timelines[timelineIndex]['show'] = event.checked;
    } else if (project?.timelines) {
      project.timelines.push({ name: type, show: event.checked });
    }

    const json: PutProject = {
      id: project?.id ?? '',
      timelines: project?.timelines,
    };

    this.projectService.setProject(json).pipe(takeUntil(this.destroy$)).subscribe((res: any) => {
      if (project) {
        this.projectService.setCurrentProject(project);
      }
    });

    if (event.checked) {
      currentValue.push(type);
    } else {
      const index = currentValue.indexOf(type);

      if (index !== -1) {
        currentValue.splice(index, 1);
      }
    }

    this.selectedTrackerTypes$.next(currentValue);
  }

  onShowEndChange() {
    this.showEndDate = !this.showEndDate;

    if (!this.startDate || !this.endDate) return;

    if (
      !this.showEndDate &&
      !isEqual(parseISO(this.endDate), addHours(startOfDay(parseISO(this.startDate)), 20))
    ) {
      this.endDate = addHours(startOfDay(parseISO(this.startDate)), 20).toISOString();

      if (this.mode === 'timeline') {
        this.getTimelineData(this.endDate);
      } else {
        this.getListData();
      }
    }

    const queryParams: Params = {
      mode: this.mode,
      startDate: this.startDate,
      endDate: this.showEndDate ? this.endDate : undefined,
    };

    this.router.navigate([], {
      queryParams: queryParams,
      queryParamsHandling: 'merge',
    });

    this.verifCanNavigateNext();
  }

  onShowUniqueTimelineChange(singleTimeline: boolean) {
    const project = this.currentProject();

    if (this.mode == 'timeline')
      this.getTimelineData(this.endDate);

    this.projectService
      .setProject({ id: project?.id, singleTimeline } as PutProject)
      .pipe(takeUntil(this.destroy$))
      .subscribe((res: any) => {
        if (project) {
          this.projectService.setCurrentProject(project);
        }

        if (res.message == 'Document updated' && project) {
          project.singleTimeline = singleTimeline;

          if (project) {
            this.projectService.setCurrentProject(project);
          }
        }
      });
  }

  getResourceNames() {
    return this.availableResources.filter((r) => r.show).map((r) => r.name).join(', ');
  }

  onTimelineDataUpdated(data: Record<string, any>) {
    if (data['metric']) {
      const metricPayload = {
        type: 'metrics',
        payload: data['metric']
      };

      this.processTimelineUpdate(metricPayload);
    }

    if (data['event']) {
      const eventPayload = {
        type: 'events',
        payload: data['event']
      };

      this.processTimelineUpdate(eventPayload);
    }

    if (data['attachment']) {
      const attachmentPayload = {
        type: 'attachments',
        payload: data['attachment']
      };

      this.processTimelineUpdate(attachmentPayload);
    }
  }

  private processTimelineUpdate(data: { type: string, payload: any }) {
    if (!data['type'] || !(['metrics', 'events', 'attachments'].includes(data['type']))) return;

    let type: string = data['type'];
    let name = data['payload']['type'] || data['payload']['name'];
    const payload = data['payload'];
    const id = payload.id;

    let index = this.timelineData[type]?.findIndex((d) => d.name == name) ?? -1;
    if (index >= 0) {
      let mIndex = this.timelineData[type]?.[index]?.data?.findIndex((c: any) => c.id == id) ?? -1;

      if (mIndex >= 0) {
        if (payload.deleted) {
          const arrayToUpdate = type === 'metrics' ? this.metrics : type === 'events' ? this.events : this.attachments;
          arrayToUpdate.splice(arrayToUpdate.findIndex((e: any) => e.id == id), 1);
          this.parseTimelineData(arrayToUpdate, type);
        } else if (this.timelineData[type]?.[index]?.data?.[mIndex]) {
          this.timelineData[type][index].data[mIndex].title = payload.payload?.title || payload.title;

          if (type == 'metrics') {
            this.timelineData[type][index].data[mIndex].label = payload.payload?.label || payload.label;
            this.timelineData[type][index].data[mIndex].color = payload.payload?.color || payload.color;
            this.timelineData[type][index].data[mIndex].group = payload.payload?.group || payload.group;

            if (payload && payload.customMetric) {
              const payloadAll = { ...payload, ...data['payload'] };
              this.timelineData[type][index].data[mIndex] = payloadAll;
            }
          }
        }
      } else {
        const arrayToUpdate: any[] = type === 'metrics' ? this.metrics : type === 'events' ? this.events : this.attachments;
        arrayToUpdate.push({ id, name, payload: data['payload'] });
        this.parseTimelineData(arrayToUpdate, type);
      }
    } else {
      const arrayToUpdate: any[] = type === 'metrics' ? this.metrics : type === 'events' ? this.events : this.attachments;
      arrayToUpdate.push({ id, name, payload: data['payload'] });
      this.parseTimelineData(arrayToUpdate, type);
    }

    this.timelineData = { ...this.timelineData };
  }

  onSelectedTagChange(tag: string) {
    this.selectedTag$.next(tag);
  }

  addMetric() {
    const dialogRef = this.dialog.open(MetricAddComponent, {
      width: '650px',
      data: {
        metricModels: this.currentProject()?.metricModels,
        create: true,
        startDate: this.startDate
      }
    });

    dialogRef.afterClosed().subscribe((res) => {
      const infoBoxWithAddFlag = { ...res, ...res.payload, type: res.name };

      this.onTimelineDataUpdated({
        type: 'metrics',
        payload: infoBoxWithAddFlag,
      });
    });
  }

  isBaseUser() {
    return ['user', 'guest'].includes(this.currentUser()?.role?.name || '');
  }

  areAllSelected(items: any[], selectedItems$: BehaviorSubject<any[]>): boolean {
    return selectedItems$.value.length === items.length;
  }

  isIndeterminate(items: any[], selectedItems$: BehaviorSubject<any[]>): boolean {
    const selectedCount = selectedItems$.value.length;
    return selectedCount > 0 && selectedCount < items.length;
  }

  toggleSelectAll(event: MatCheckboxChange, items: any[], selectedItems$: BehaviorSubject<any[]>): void {
    if (event.checked) {
      selectedItems$.next([...items]);
    } else {
      selectedItems$.next([]);
    }
  }

  configureMetricModels() {
    this.router.navigate(['/project/project-settings/informations']);
  }

  clearTarget() {
    this.target.set('');

    const queryParams: Params = {
      mode: this.mode,
      startDate: this.startDate || undefined,
      endDate: this.showEndDate ? this.endDate : undefined,
      target: null
    };

    this.router.navigate([], {
      queryParams: queryParams,
      queryParamsHandling: 'merge',
    });
  }
}
